import { Button, Switch, Checkbox, Space, Radio, Row, Form, Col, Input, InputNumber, Select, Typography } from 'antd';
import React, { ReactElement, useState, useRef, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import NumberFormat from 'react-number-format';
import { useMount } from 'react-use';

import EnumSelector from 'vcomponents/Form/EnumSelector';
import EnumRadio from 'vcomponents/Form/EnumRadio';
import CheckboxGroup from 'vcomponents/Form/CheckboxGroup';
import { ConditionalWrapper } from 'utils/view';

import * as TextDeco from 'decorators/text';
import * as ViewDeco from 'decorators/views';

import callServer from 'apis/server';

import objC from 'utils/data/obj';
import { FormattedMessageFixed } from 'vendys-commons/utils';

import { Content, MainContent, ActionContent, RowWrapper } from './styles';

const Decorators = { ...TextDeco, ...ViewDeco };

export interface FormBuilderProps {
  name?: string;
  ns?: string;
  columns?: any[];
  options?: {};
  data?: any;
  defaultValues?: object;
  onClick?: (v) => object;
  onValuesChange?: (k, v, a) => void;
  colInRow?: number;
  vertical?: boolean;
  layout?: 'vertical' | 'horizontal';
  setForm?: (f) => {};
}

const view = {};
const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 }
};
const renderRow = (props) => {
  var {
    id,
    label,
    type,
    data,
    list = [],
    name,
    viewProps = {},
    View,
    help,
    helpType,
    placeholder,
    validateTrigger,
    children,
    layout,
    rules = [],
    required = false,
    defaultValue,
    onChange,
    defaultLabel,
    extra,
    helperText
  } = props;
  let content = null;

  switch (type) {
    case 'enum':
      content = (
        <EnumSelector
          id={id}
          data={data}
          onChange={onChange}
          defaultValue={defaultValue}
          defaultLabel={defaultLabel}
          {...viewProps}
        />
      );
      break;

    case 'select':
      content = (
        <Select id={id} onChange={onChange} defaultValue={defaultValue} {...viewProps}>
          {list.map(({ value, label: l }, i) => (
            <Select.Option key={value || `key-${i}`} value={value}>
              {l}
            </Select.Option>
          ))}
        </Select>
      );
      break;

    case 'radio':
      content = (
        <Radio.Group id={id} onChange={onChange} defaultValue={defaultValue}>
          <ConditionalWrapper
            condition={layout === 'vertical'}
            wrapper={(children) => <Space direction="vertical">{children}</Space>}
          >
            {list.map(({ value, label }) => {
              return <Radio value={value}>{label}</Radio>;
            })}
          </ConditionalWrapper>
        </Radio.Group>
      );
      break;

    case 'EnumRadio':
      content = (
        <EnumRadio
          id={id}
          data={data}
          onChange={onChange}
          defaultValue={defaultValue}
          defaultLabel={defaultLabel}
          {...viewProps}
        />
      );
      break;

    case 'checkbox':
      //   content = <Checkbox.Group id={id}  options={list} defaultValue={defaultValue} onChange={onChange} />
      content = (
        <Checkbox id={id} options={list} defaultValue={defaultValue} onChange={onChange} {...viewProps}>
          <FormattedMessage id={viewProps.msgId || ''} />
        </Checkbox>
      );
      break;

    case 'checkboxes':
      //   content = <Checkbox.Group id={id}  options={list} defaultValue={defaultValue} onChange={onChange} />
      content = <CheckboxGroup id={id} options={list} defaultValue={defaultValue} onChange={onChange} {...viewProps} />;
      break;

    case 'text':
      break;
    case 'string':
      content = (
        <Input
          id={id}
          onChange={onChange}
          defaultValue={defaultValue}
          style={{}}
          {...viewProps}
          placeholder={viewProps.placeholder}
        />
      );
      break;

    case 'number':
      content = <InputNumber id={id} onChange={onChange} defaultValue={defaultValue} style={{}} />;
      break;

    case 'switch':
      content = <Switch id={id} onChange={onChange} defaultValue={defaultValue} style={{}} />;
      break;

    case 'email':
      content = (
        <Input
          id={id}
          onChange={onChange}
          defaultValue={defaultValue}
          style={{}}
          {...viewProps}
          placeholder={viewProps.placeholder}
        />
      );
      if (!rules.length || !rules.some(({ type: t }) => t && t === 'email')) {
        rules.push({ type: 'email' });
      }
      break;

    case 'phone':
      content = (
        <NumberFormat
          id={id}
          onValueChange={onChange}
          format="###-####-####"
          mask="_"
          placeholder={viewProps.placeholder}
        />
      );
      break;

    case 'textarea':
      content = (
        <Input.TextArea
          id={id}
          onChange={onChange}
          defaultValue={defaultValue}
          style={{}}
          {...viewProps}
          placeholder={viewProps.placeholder}
        />
      );
      break;

    case 'crn':
      content = (
        <NumberFormat
          id={id}
          onValueChange={onChange}
          format="###-##-#####"
          mask="_"
          placeholder={viewProps.placeholder}
        />
      );
      break;

    case 'component':
      content = React.cloneElement(data, { ...viewProps, onChange });
      break;

    case 'FormattedMessage':
      var _View = Decorators[type];
      content = <_View id={id} onChange={onChange} {...viewProps} />;
      break;

    case 'custom':
      content = React.cloneElement(View, { ...View.props, ...viewProps });
      break;

    default:
      var _View = Decorators[type];

      content =
        _View['type'] && _View['type']['$$typeof'] ? (
          React.cloneElement(_View, { onChange, ...viewProps, placeholder: viewProps.placeholder })
        ) : (
          <_View id={id} onChange={onChange} {...viewProps} placeholder={viewProps.placeholder} />
        );
      break;
  }

  return (
    <Form.Item
      key={label}
      name={name}
      help={help ? <FormattedMessage id={help} defaultMessage={help} /> : undefined}
      label={label}
      rules={rules}
      extra={extra}
      validateTrigger={validateTrigger}
    >
      {content}
    </Form.Item>
  );
};

const FormBuilder: React.FC<FormBuilderProps> = (props) => {
  var {
    columns = [],
    onClick = () => {},
    colInRow = 1,
    theme = 'default',
    ns,
    children,
    setForm = () => {},
    vertical = true,
    defaultValues = {},
    onValuesChange = (k, v, a) => {},
    data,
    ...other
  } = props;
  var [_data, setData] = useState({ ...defaultValues, ...data });
  const [form] = Form.useForm();
  const intl = useIntl();

  useEffect(() => {
    if (!form) return;
    setForm(form);
  }, [form]);

  const _columns = useMemo(() => {
    var __columns = columns.map((column) => {
      var { rules = [] } = column;
      if (column.viewProps && column.viewProps.placeholder) {
        try {
          column.viewProps.placeholder = intl.formatMessage({ id: column.viewProps.placeholder });
        } catch (e) {
          column.viewProps.placeholder = column.viewProps.placeholder;
        }
      }

      var label = column.label;
      if (!label) {
        label = column.id;
      }
      var labelMsg = ns ? `${ns}.${label}` : label;
      const msg = intl.formatMessage({ id: labelMsg });
      label = msg || label;

      if (rules.length > 0) {
        rules.forEach((rule) => {
          const { message } = rule;
          if (message) {
            const msg = intl.formatMessage({ id: message });
            rule.message = msg || message;
          }
        });
      }
      return { ...column, label };
    });

    return __columns;
  }, [columns]);

  const _children = useMemo(() => {
    var chO = {};
    if (!children) return chO;

    if (children.$$typeof) {
      chO[children.props.id] = children;
      return chO;
    }
    children.map((child) => {
      const { props } = child;
      if (props.id) {
        chO[props.id] = child;
      }
    });
    return chO;
  }, [children]);

  const _onClick = () => {
    onClick(_data);
  };

  const _onItemChange = (id, v) => {
    let value = v;

    if (v && v.target) {
      value = v.target.value;
    }

    const nd = { ..._data, [id]: value };
    setData(nd);
    onValuesChange(id, value, nd);
  };

  useEffect(() => {
    if (!form) return;
    form.setFieldsValue(data);
  }, [data]);

  useEffect(() => {}, [_data]);

  return (
    <Content className={theme}>
      <MainContent className={theme}>
        <Form {...other} {...layout} style={{ display: 'flex', flex: 1 }} form={form}>
          <RowWrapper gutter={24} style={{ marginLeft: 0, marginRight: 0, flex: 1 }} className={theme}>
            {_columns.map((row, i) => {
              var { id, span } = row;
              var dv = defaultValues[id];
              var width = span ? Math.floor(24 / colInRow) * span : Math.floor(24 / colInRow);
              var View = _children[id];

              if (View) {
                return (
                  <Col span={width} key={i}>
                    {renderRow({
                      ...row,
                      data,
                      type: 'custom',
                      View,
                      defaultValue: dv,
                      onChange: (v) => _onItemChange(id, v)
                    })}
                  </Col>
                );
              }

              return (
                <Col span={width} key={i}>
                  {renderRow({ ...row, defaultValue: dv, onChange: (v) => _onItemChange(id, v) })}
                </Col>
              );
            })}
          </RowWrapper>
        </Form>
      </MainContent>
    </Content>
  );
};

export const FormBuilderMin = (props) => {};

export default FormBuilder;
